import { fetchUtils } from 'react-admin';

import config from './config/config';

const apiUrl = config.authApiUrl; 

const getHeaders = () => new Headers({
    'Content-Type': 'application/json',
    'Origin': window.location.origin, // Dynamically set the origin for CORS
});

export const authProvider = {
    login: ({ username, password, tenant }) => {
        const request = new Request(`${apiUrl}/signin`, {
            method: 'POST',
            body: JSON.stringify({ username, password, tenant }),
            headers: getHeaders(),
            mode: 'cors', // Explicitly set the mode to 'cors'
        });
        return fetch(request)
            .then(response => {
                if (response.status < 200 || response.status >= 300) {
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then(auth => {
                localStorage.setItem('auth', JSON.stringify({ ...auth, tenant }));
            });
    },
    
    signup: ({ email, password, tenant }) => {
        const request = new Request(`${apiUrl}/signup`, {
            method: 'POST',
            body: JSON.stringify({ email, password, tenant }),
            headers: getHeaders(),
            mode: 'cors', // Explicitly set the mode to 'cors'
        });
        return fetch(request)
            .then(response => {
                if (response.status < 200 || response.status >= 300) {
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then(auth => {
                // Optionally handle any actions after signup, e.g., auto-login
                localStorage.setItem('auth', JSON.stringify({ ...auth, tenant }));
            });
    },
    // Verify code function
    verify_code: ({ username, confirmation_code, tenant }) => {
      const request = new Request(`${apiUrl}/confirm-signup`, {
          method: 'POST',
          body: JSON.stringify({ username, confirmation_code, tenant }),
          headers: getHeaders(),
          mode: 'cors', // Explicitly set the mode to 'cors'
      });
      return fetch(request)
          .then(response => {
              if (response.status < 200 || response.status >= 300) {
                  throw new Error(response.statusText);
              }
              return response.json();
          });
    },
    
    logout: () => {
        localStorage.removeItem('auth');
        return Promise.resolve();
    },
    
    checkError: (error) => {
        const status = error.status;
        if (status === 401 || status === 403) {
            localStorage.removeItem('auth');
            return Promise.reject();
        }
        return Promise.resolve();
    },
    
    checkAuth: () => {
        const auth = JSON.parse(localStorage.getItem('auth'));
        if (auth && auth.access_token) {
            return Promise.resolve();
        }
        return Promise.reject();
    },
    
    getPermissions: () => {
        const auth = JSON.parse(localStorage.getItem('auth'));
        return auth ? Promise.resolve(auth.permissions) : Promise.reject();
    },
    
    getIdentity: () => {
        const auth = JSON.parse(localStorage.getItem('auth'));
        return auth ? Promise.resolve(auth.sub) : Promise.reject();
    },
    
    refreshAuth: () => {
        const auth = JSON.parse(localStorage.getItem('auth'));
        if (!auth || !auth.refresh_token || !auth.sub) {
            return Promise.reject('No refresh token or sub available');
        }
        const request = new Request(`${apiUrl}/refresh-token`, {
            method: 'POST',
            body: JSON.stringify({ refresh_token: auth.refresh_token, sub: auth.sub }),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        return fetch(request)
            .then(response => {
                if (response.status < 200 || response.status >= 300) {
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then(newAuth => {
                localStorage.setItem('auth', JSON.stringify(newAuth));
                return newAuth.token;
            });
    },
};

// Custom fetch with refresh token logic
export const httpClient = async (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    const auth = JSON.parse(localStorage.getItem('auth'));
    if (auth && auth.token) {
        options.headers.set('Authorization', `Bearer ${auth.token}`);
    }
    return fetchUtils.fetchJson(url, options).catch(async (error) => {
        if (error.status === 401) {
            // Token has expired
            try {
                const newToken = await authProvider.refreshAuth();
                options.headers.set('Authorization', `Bearer ${newToken}`);
                return fetchUtils.fetchJson(url, options);
            } catch (refreshError) {
                // Unable to refresh, user must login again
                await authProvider.logout();
                throw refreshError;
            }
        }
        throw error;
    });
};
